import { DateField, SelectField } from 'payload'
import { mergeObjects } from '@utils/objects';
import { FieldDefault, FieldWithouType } from '@utils/fields'
import { createOptionsForTimeRange } from '@utils/dates';

/**
 * Creates a DateAndTime for Payload
 *
 * @param config
 * @returns
 */
export function datetimeField(config: FieldWithouType<CustomPayloadField<DateField>>): DateField[] {
  return dateField(config, true);
}

/**
 * Creates a Date for Payload
 *
 * @param config
 * @param withHours
 * @returns
 */
export function dateField(config: FieldWithouType<CustomPayloadField<DateField>>, withHours: boolean = false): DateField[] {
  const field: FieldDefault<CustomPayloadField<DateField>> = {
    type: 'date',
    admin: {
      date: {
        pickerAppearance: withHours ? 'dayAndTime' : 'dayOnly',
        displayFormat: withHours ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy',
        timeFormat: 'HH:mm',
        timeIntervals: 60,
        overrides: {
          timeCaption: 'Hora',
        }
      },
    },
  };

  return [ mergeObjects(config, field) ];
}

/**
 * Creates a Time for Payload
 *
 * @param config
 * @returns
 */
export function timeField(config: FieldWithouType<CustomPayloadField<DateField>>): DateField[] {
  const field: FieldDefault<CustomPayloadField<DateField>> = {
    type: 'date',
    admin: {
      className: 'time-field',
      date: {
        pickerAppearance: 'timeOnly',
        displayFormat: 'HH:mm',
        timeFormat: 'HH:mm',
        timeIntervals: 15,
        overrides: {
          showTimeCaption: false,
        }
      },
    },
  };

  return [ mergeObjects(config, field) ];
}

export type TimeSelectProps = {
  displayFormat?: string;
  timeIntervals?: number;
  startTime?: string;
  endTime?: string;
};

/**
 * Creates a Time (text) for Payload
 *
 * @param config
 * @returns
 */
export function timeSelectField(config: TimeSelectProps & Omit<FieldWithouType<CustomPayloadField<SelectField>>, 'options'>): SelectField[] {
  const {
    displayFormat = 'HH:mm',
    timeIntervals = 15,
    startTime = '00:00',
    endTime = '23:59',
    ...props
  } = config;

  const allTimeOptions = createOptionsForTimeRange(startTime, endTime, timeIntervals, displayFormat);

  const defaultConfig: FieldDefault<CustomPayloadField<SelectField>> = {
    type: 'select',
    options: allTimeOptions,
  };

  const field = mergeObjects(props, defaultConfig);
  return [ field ];
}

/**
 * Creates a Month/Year for Payload
 *
 * @param config
 * @returns
 */
export function monthField(config: FieldWithouType<CustomPayloadField<DateField>>): DateField[] {
  const field: FieldDefault<CustomPayloadField<DateField>> = {
    type: 'date',
    admin: {
      date: {
        pickerAppearance: 'monthOnly',
        displayFormat: 'MM/yyyy',
      },
    },
  };

  return [ mergeObjects(config, field) ];
}

export type MonthSelectorProps = {
  allowMultiple?: boolean,
  showNumbers?: boolean
};

/**
 * Creates a Month Selector for Payload
 *
 * @param config
 * @param withHours
 * @returns
 */
export function monthSelectorField(config: FieldWithouType<CustomPayloadField<DateField>>, options: MonthSelectorProps = {} ): SelectField[] {
  const { allowMultiple = false, showNumbers = false } = options;

  const description = config?.admin?.description || 'Escolha um dos meses.';

  const field: FieldDefault<SelectField> = {
    type: 'select',
    hasMany: allowMultiple,
    options: [
      { value: '1', label: `${showNumbers ? '01 - ' : ''}Janeiro` },
      { value: '2', label: `${showNumbers ? '02 - ' : ''}Fevereiro` },
      { value: '3', label: `${showNumbers ? '03 - ' : ''}Março` },
      { value: '4', label: `${showNumbers ? '04 - ' : ''}Abril` },
      { value: '5', label: `${showNumbers ? '05 - ' : ''}Maio` },
      { value: '6', label: `${showNumbers ? '06 - ' : ''}Junho` },
      { value: '7', label: `${showNumbers ? '07 - ' : ''}Julho` },
      { value: '8', label: `${showNumbers ? '08 - ' : ''}Agosto` },
      { value: '9', label: `${showNumbers ? '09 - ' : ''}Setembro` },
      { value: '10', label: `${showNumbers ? '10 - ' : ''}Outubro` },
      { value: '11', label: `${showNumbers ? '11 - ' : ''}Novembro` },
      { value: '12', label: `${showNumbers ? '12 - ' : ''}Dezembro` },
    ],
    admin: {
      description: undefined,
      components: {
        afterInput: [
          {
            path: '@components/(payload)/fields/select/month-periodicity-selector',
            clientProps: { description }
          }
        ],
      },
    }
  };

  return [ mergeObjects(config, field) ];
}
